import { InfoIcon } from 'lucide-react'
import { useId } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { HAZMAT_SWITCH_LABEL } from '@/modules/carriers/lib/hazmatTarget'
import { useRenewal } from '../../hooks/renewal-provider'

/**
 * CRD-009: the Hazmat status for a renewal or re-registration. It starts at the certificate's
 * current status and can go either way. The insurance check and the fees follow the choice, and
 * it is applied to the certificate when the payment completes. Shown only to users the server
 * lets choose (staff and the carrier's owners).
 */
export function RenewalHazmatChoice({ className }: { className?: string }) {
  const switchId = useId()
  const { hazmat, hazmatChoice, setHazmatChoice, isReRegistration } =
    useRenewal()

  if (!hazmat?.canChooseOnRenewal) return null

  const isHazmat = hazmatChoice ?? hazmat.effectiveHazmat
  const flow = isReRegistration ? 're-registration' : 'renewal'

  return (
    <Alert variant="info" className={className}>
      <InfoIcon />
      <AlertTitle>Hazmat Designation</AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <p>
          Your Hazmat designation determines the insurance this certificate
          requires and the fees for this {flow}. A change takes effect when your
          payment completes.
        </p>
        <div className="flex items-center gap-3">
          <Label htmlFor={switchId}>{HAZMAT_SWITCH_LABEL}</Label>
          <span className="inline-flex items-center gap-2">
            <Switch
              id={switchId}
              checked={isHazmat}
              onCheckedChange={setHazmatChoice}
            />
            <span aria-hidden="true" className="font-semibold">
              {isHazmat ? 'Yes' : 'No'}
            </span>
          </span>
        </div>
      </AlertDescription>
    </Alert>
  )
}
